import * as React from "react";
import * as galleryActions from "modules/gallery/actionCreator";
import { connect, ConnectedProps } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import Loading from "modules/common/components/loading";
import { Document } from "modules/documents/models";
import { AttachedFile } from "modules/gallery/models";
import Uploader from "../uploader/uploader";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import Pagination from '@mui/material/Pagination';
import CloseIcon from "@mui/icons-material/Close";
import DocumentGallerySearch from "./documentGallerySearch";
import { SortStyle } from "utils/managementUtils";
import ManagementEmptyResults from "../../managementEmptyResults";
import ShallowEquals from "utils/shallowEquals";
import { AttachmentType } from "modules/common/models";
import LoadingCircle from "../../loadingCircle";
import { FileUploadData, FileUploadReturn } from "../models";

import "./documentGallery.sass";

class DocumentGallery extends React.Component<PropsWithRedux, ComponentState> {
  private MAX_SELECTABLE_FILES: number = 20;

  constructor(props: PropsWithRedux) {
    super(props);

    this.state = {
      isUploading: false,
      selectableFileCount: this.getSelectableFileCount(),
      selectedFiles: this.getSelectedFiles(),
      selectedTab: 0,
      sortStyle: SortStyle.titleAsc,
      searchText: "",
      showSelectedFilesMaxSizeWarning: this.shouldShowMaxSizeWarningForSelectedFiles(this.getSelectedFiles()),
      showUploadedFilesMaxSizeWarning: false
    };
  }

  componentDidUpdate(prevProps: PropsWithRedux, prevState: ComponentState) {
    if (this.props.show && !prevProps.show) {
      this.setState({
        ...this.state,
        selectableFileCount: this.getSelectableFileCount(),
        selectedFiles: this.getSelectedFiles(),
        selectedTab: 0,
        showSelectedFilesMaxSizeWarning: this.shouldShowMaxSizeWarningForSelectedFiles(this.getSelectedFiles()),
        showUploadedFilesMaxSizeWarning: false
      });

      this.props.getDocumentGallery(1, "", this.state.sortStyle);
    }
    else if (
      !ShallowEquals(prevState.selectedFiles.map(f => f.blobId), this.state.selectedFiles.map(f => f.blobId))
    ) {
      this.setState({ 
        showSelectedFilesMaxSizeWarning: this.shouldShowMaxSizeWarningForSelectedFiles(this.state.selectedFiles)
      });
    }
  }

  public render() {
    const { documentGallery, totalFileAttachmentsMBSizeLimit } = this.props;

    return (
      <Dialog open={this.props.show} maxWidth={false} onClose={this.onClose}>
        <DialogTitle className="events-dialog-header">
          <div className="events-dialog-header-title">
            <div>
              <Typography variant="h2">Select Documents</Typography>
            </div>
            <IconButton onClick={this.onClose} size="large">
              <CloseIcon />
            </IconButton>
          </div>
          <Tabs
            value={this.state.selectedTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={this.onSelectTab}
          >
            <Tab label="Use existing media" />
            <Tab label="Upload" />
          </Tabs>
        </DialogTitle>
        <DialogContent dividers className="authoring-gallery-content">
          {this.state.selectedTab === 0 &&
            <React.Fragment>
              <DocumentGallerySearch 
                sortStyle={this.state.sortStyle}
                onChangeSort={this.onChangeSort}
                onUpdateText={this.onChangeText}
                text={this.state.searchText}
                disabled={documentGallery.isFetching}
              />
              {documentGallery.isFetching
                ? <Loading padding={12} />
                :
                  <div>
                    {documentGallery.totalDocuments !== 0
                    ? <div id="authoring-gallery-documents-container" className="authoring-gallery-items authoring-gallery-documents">
                      {documentGallery.documents.map((document: Document, idx: number) => {
                        const isSelectedIndex: number = this.state.selectedFiles.findIndex((selectedFile) => selectedFile.ospreyId === document.id);
                        const isSelected: boolean = isSelectedIndex > -1;
                        return (
                          <div id={`authoring-gallery-document-${idx}`} key={document.id} onClick={() => this.onSelectDocument(document, !isSelected)}>
                            <div className={`authoring-gallery-document ${isSelected ? "item-selected" : ""}`}>
                              <img src={`/images/icons/documents/${document.fileType}.png`} alt={document.fileType} />
                              {isSelected &&
                                <div className="item-selected-number">{isSelectedIndex + 1}</div>
                              }
                            </div>
                            <div id={`authoring-gallery-document-name-${idx}`} className="document-name">{document.title || `${document.fileName}.${document.fileType}`}</div>
                          </div>
                        );
                      })}
                    </div>
                    : <div> 
                      <ManagementEmptyResults searchType="matches" hasFilters={false} height="60vh" hidePaging/>
                    </div> 
                    }
                    <Pagination color="primary" count={documentGallery.totalPages} page={documentGallery.currentPage} boundaryCount={2} showFirstButton showLastButton onChange={this.onGetDocumentGalleryPage} />
                  </div>
              }
            </React.Fragment>
          }
          {this.state.selectedTab === 1 &&
            <Uploader
              uploaderMessage={`Choose or drag up to ${this.state.selectableFileCount} files here`}
              maxUploadable={this.state.selectableFileCount}
              uploading={this.state.isUploading}
              setUploading={this.onSetUploading}
              uploadAll={this.onUploadAll}
              onAllComplete={this.onClose}
              attachmentType={AttachmentType.Files}
              acceptedFiles="application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,application/pdf,image/png,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              exceededSizeLimit={this.hasUploadedFilesExceededSizeLimit}
            />
          }
        </DialogContent>
        <DialogActions className="events-dialog-footer">
          {
            totalFileAttachmentsMBSizeLimit 
            ? <>
                <span className={`events-dialog-footer-text ${this.shouldShowMaxSizeWarning() ? "max-size-warning" : ""}`}>
                  {`Total attachments must be ${totalFileAttachmentsMBSizeLimit}MB or less.`}
                </span>
                <div className="events-dialog-footer-buttons-container">
                  {this.getBaseActionButtons(true)}
                </div>
            </>
            : this.getBaseActionButtons()
          }
        </DialogActions>
      </Dialog>
    );
  }

  private hasUploadedFilesExceededSizeLimit = (totalFilesSize: number): boolean => {
    const hasExceeded = this.props.totalFileAttachmentsMBSizeLimit !== undefined && 
        this.props.totalFileAttachmentsMBSizeLimit > 0 &&
        totalFilesSize > (this.props.totalFileAttachmentsMBSizeLimit * 1024 * 1024);

    this.setState({ showUploadedFilesMaxSizeWarning: hasExceeded });

    return hasExceeded;
  }

  private getSelectableFileCount = (): number => {
    return this.MAX_SELECTABLE_FILES - (this.props.fileAttachments || []).length;
  }

  private onChangeSort = (sort: SortStyle) => {
    this.setState({sortStyle: sort});
    this.onGetDocumentGalleryPage(null, 1, this.state.searchText, sort);
  }

  private onChangeText = (inputText: string) => {
    this.setState({searchText: inputText});
    this.onGetDocumentGalleryPage(null, 1, inputText, this.state.sortStyle);
  }

  private getSelectedFiles = (): AttachedFile[] => {
    return this.props.fileAttachments || [];
  }

  private onApply = () => {
    this.props.onApply(this.state.selectedFiles);
  }

  private onClose = () => {
    this.props.onClose();
  }

  private onGetDocumentGalleryPage = (ev, pageNumber: number, text?: string, sort?: SortStyle) => {
    let textToUse = text ?? this.state.searchText;
    let sortToUse = sort ?? this.state.sortStyle;

    this.props.getDocumentGallery(pageNumber, textToUse, sortToUse);
  }

  private onSelectDocument = (document: Document, select: boolean) => {
    if (select) {
      const selectedFile: AttachedFile = {
        blobId: document.id,
        fileExtension: document.fileType,
        fileName: document.title || `${document.fileName}.${document.fileType}`,
        fileType: "application",
        fileUrl: "",
        ospreyId: document.id,
        fileSize: document.fileSize
      };
      if (this.state.selectableFileCount > 0)
        this.setState({ selectedFiles: this.state.selectedFiles.concat([selectedFile]) });
    } else {
      this.setState({ selectedFiles: this.state.selectedFiles.filter((selectedImage) => selectedImage.ospreyId !== document.id) });
    }
  }

  private onSelectTab = (ev, tabIndex: number) => {
    this.setState({ 
      ...this.state, 
      selectedTab: tabIndex
    });
  }

  private onSetUploading = (isUploading: boolean) => {
    this.setState({ ...this.state, isUploading });
  }

  private onUploadAll = (files: FileUploadData[]): Promise<FileUploadReturn[] | null> => {
    if (files.length === 0) {
      return Promise.resolve(null);
    }

    let documentUploadReq = files.map((fileData) => {
      const file: File = fileData.file;
      return this.props.uploadDocument(file, fileData.onUploadProgress!, fileData.onUploadComplete!, fileData.onUploadError!, fileData.onUploadAbort!)
    });

    return Promise.all(documentUploadReq)
      .then((documents) => {
        const fileAttachments: AttachedFile[] = this.props.fileAttachments || [];
        const newFiles: AttachedFile[] = documents.map((document)=> ({
          blobId: document.blobId,
          fileUrl: "",
          fileName: document.title || `${document.fileName}.${document.fileType}`,
          fileType: "application",
          fileExtension: document.fileType,
          ospreyId: document.id,
          fileSize: document.fileSize
        }));

        this.props.onApply(fileAttachments.concat(newFiles));

        const fileReturn: FileUploadReturn[] = documents.map((document, idx) => ({
          ...document,
          documentUploadType: files[idx].documentUploadType,
        }));

        return fileReturn;
      });
  }

  private getBaseActionButtons = (withMargin?: boolean): React.JSX.Element => {
    return <>
        <Button
          id="cancel-documents-button"
          variant="text" 
          color="primary" 
          onClick={this.onClose}
          className={`${withMargin ? "with-margin" : ""}`}
        >
          Cancel
        </Button>
        <Button 
          id="apply-documents-button" 
          variant="contained" 
          color="primary" 
          disabled={
            !this.state.selectedFiles.length || 
            this.state.showSelectedFilesMaxSizeWarning ||
            !!this.props.isLoadingApplyButton
          } 
          onClick={this.onApply}
        >
          {
            this.props.isLoadingApplyButton
              ? <LoadingCircle size={20} />
              : "Apply"
          }
        </Button>
    </>
  }

  private shouldShowMaxSizeWarning = (): boolean => 
    (this.state.selectedTab === 0 && this.state.showSelectedFilesMaxSizeWarning) ||
    (this.state.selectedTab === 1 && this.state.showUploadedFilesMaxSizeWarning);

  private shouldShowMaxSizeWarningForSelectedFiles = (selectedFiles: AttachedFile[]): boolean => {
    const { totalFileAttachmentsMBSizeLimit } = this.props;
    
    if (totalFileAttachmentsMBSizeLimit && totalFileAttachmentsMBSizeLimit > 0) {
      const totalSelectedFilesSize = selectedFiles.reduce((acc, file) => acc + (file.fileSize || 0), 0);

      return (totalSelectedFilesSize / (1024 * 1024)) > totalFileAttachmentsMBSizeLimit;
    }

    return false;
  }
}


interface ComponentProps {
  show: boolean;
  fileAttachments?: AttachedFile[];
  onApply: (fileAttachments: AttachedFile[]) => void;
  onClose: () => void;
  totalFileAttachmentsMBSizeLimit?: number;
  isLoadingApplyButton?: boolean;
}

interface ComponentState {
  isUploading: boolean;
  selectableFileCount: number;
  selectedFiles: AttachedFile[];
  selectedTab: number;
  sortStyle: SortStyle;
  searchText: string;
  showSelectedFilesMaxSizeWarning: boolean;
  showUploadedFilesMaxSizeWarning: boolean;
}

const connector = connect(
  (state: GlobalApplicationState, ownProps: ComponentProps) => ({
    ...ownProps,
    documentGallery: state.gallery.documentGallery
  }), {
    getDocumentGallery: galleryActions.getDocumentGallery,
    uploadDocument: galleryActions.uploadDocument
  }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(DocumentGallery);
